import type { RouteRecordRaw } from 'vue-router';
import { ANZEIGENAME, KATEGORIE } from '@/persistence/RechteStorage';
import { createStepGuard } from '@/router/utils.ts';
import { useMehrAugenPrinzipStore } from './views/turnOnMehraugen/useTurnOnMehraugenStore.ts';
import { NutzerAuswahlTyp, RollenAuswahlTyp } from '@/auftraggeber/types/Einstellung.ts';
export const EINSTELLUNGEN_ROUTE_NAMES = {
  BASE: 'einstellungen',
  EINSTELLUNGEN: 'einstellungen-uebersicht',
  MEHR_AUGEN_PRINZIP_AKTIVIEREN_BASE: 'einstellungen-mehr-augen-prinzip-aktivieren-base',
  MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS: 'einstellungen-mehr-augen-prinzip-aktivieren-hinweis',
  MEHR_AUGEN_PRINZIP_AKTIVIEREN_AUSWAHL_ROLLENART:
    'einstellungen-mehr-augen-prinzip-aktivieren-bestaetigung',
  MEHR_AUGEN_PRINZIP_AKTIVIEREN_ROLLE_NEU: 'einstellungen-mehr-augen-prinzip-aktivieren-rolle-neu',
  MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZERART_AUSWAHL:
    'einstellungen-mehr-augen-prinzip-aktivieren-nutzer-auswahl',
  MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZER_NEU:
    'einstellungen-mehr-augen-prinzip-aktivieren-nutzer-neu',
  MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZER_BESTEHEND:
    'einstellungen-mehr-augen-prinzip-aktivieren-nutzer-bestehend',
} as const;

const ALLE_FLOW_ROUTES = [
  EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS,
  EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_AUSWAHL_ROLLENART,
  EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_ROLLE_NEU,
  EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZERART_AUSWAHL,
  EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZER_NEU,
  EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZER_BESTEHEND,
];

export const einstellungenRoutes: RouteRecordRaw = {
  name: EINSTELLUNGEN_ROUTE_NAMES.BASE,
  path: 'einstellungen',
  redirect: { name: EINSTELLUNGEN_ROUTE_NAMES.EINSTELLUNGEN },
  children: [
    {
      path: '',
      name: EINSTELLUNGEN_ROUTE_NAMES.EINSTELLUNGEN,
      component: () => import('./views/turnOnMehraugen/EinstellungenView.vue'),
      meta: {
        kategorie: [KATEGORIE.UNTERNEHMEN],
        anzeigeName: ANZEIGENAME.VERWALTEN,
      },
    },
    {
      name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_BASE,
      path: 'mehr-augen-prinzip/aktivieren',
      redirect: { name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS },
      component: () => import('./views/turnOnMehraugen/MehraugenAktivierenPage.vue'),
      children: [
        {
          name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS,
          path: 'hinweis',
          component: () => import('./views/turnOnMehraugen/AktivierenHinweis.vue'),
        },
        {
          name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_AUSWAHL_ROLLENART,
          path: 'bestaetigung',
          component: () => import('./views/turnOnMehraugen/AuswahlRollenart.vue'),
          beforeEnter: createStepGuard(
            ALLE_FLOW_ROUTES,
            EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS,
          ),
        },
        {
          name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_ROLLE_NEU,
          path: 'rolle-neu',
          component: () => import('./views/turnOnMehraugen/RolleNeu.vue'),
          beforeEnter: [
            createStepGuard(
              ALLE_FLOW_ROUTES,
              EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS,
            ),
            () => {
              const store = useMehrAugenPrinzipStore();
              if (store.rollenAuswahlTyp !== RollenAuswahlTyp.NEU) {
                return {
                  name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_AUSWAHL_ROLLENART,
                };
              }
            },
          ],
        },
        {
          name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZERART_AUSWAHL,
          path: 'nutzer-auswahl',
          component: () => import('./views/turnOnMehraugen/AuswahlNutzerart.vue'),
          beforeEnter: createStepGuard(
            ALLE_FLOW_ROUTES,
            EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS,
          ),
        },
        {
          name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZER_NEU,
          path: 'nutzer-neu',
          component: () => import('./views/turnOnMehraugen/NutzerNeu.vue'),
          beforeEnter: [
            createStepGuard(
              ALLE_FLOW_ROUTES,
              EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS,
            ),
            () => {
              const store = useMehrAugenPrinzipStore();
              if (store.nutzerAuswahlTyp !== NutzerAuswahlTyp.NEU) {
                return {
                  name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZERART_AUSWAHL,
                };
              }
            },
          ],
        },
        {
          name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZER_BESTEHEND,
          path: 'nutzer-bestehend',
          component: () => import('./views/turnOnMehraugen/NutzerBestehend.vue'),
          beforeEnter: [
            createStepGuard(
              ALLE_FLOW_ROUTES,
              EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_HINWEIS,
            ),
            () => {
              const store = useMehrAugenPrinzipStore();
              if (store.nutzerAuswahlTyp !== NutzerAuswahlTyp.BESTEHEND) {
                return {
                  name: EINSTELLUNGEN_ROUTE_NAMES.MEHR_AUGEN_PRINZIP_AKTIVIEREN_NUTZERART_AUSWAHL,
                };
              }
            },
          ],
        },
      ],
    },
  ],
};
